import React, { useState } from 'react';

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayCheckoutOptions) => {
      open: () => void;
      on: (
        event: string,
        callback: (response: {
          error?: {
            code?: string;
            description?: string;
            reason?: string;
          };
        }) => void
      ) => void;
    };
  }
}

export interface RazorpayCheckoutResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayCheckoutOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayCheckoutResponse) => void | Promise<void>;
  modal?: {
    ondismiss?: () => void;
  };
  theme?: {
    color?: string;
  };
  retry?: {
    enabled: boolean;
  };
}

interface RazorpayCheckoutProps {
  orderId: string;
  keyId: string;
  amountPaise: number;
  currency?: string;
  description: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onPaymentResponse: (
    response: RazorpayCheckoutResponse
  ) => void | Promise<void>;
  onDismiss?: () => void;
  onError?: (message: string) => void;
}

let checkoutScriptPromise: Promise<void> | null = null;

function loadRazorpayCheckout(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Checkout is available only in a browser.'));
  }

  if (window.Razorpay) return Promise.resolve();

  if (!checkoutScriptPromise) {
    checkoutScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-razorpay-checkout="true"]'
      );

      const script = existing || document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.dataset.razorpayCheckout = 'true';

      script.onload = () => {
        if (window.Razorpay) {
          resolve();
        } else {
          checkoutScriptPromise = null;
          reject(new Error('Razorpay Checkout loaded without its SDK.'));
        }
      };

      script.onerror = () => {
        checkoutScriptPromise = null;
        reject(new Error('Could not load Razorpay Checkout. Check your connection.'));
      };

      if (!existing) document.head.appendChild(script);
      else if (window.Razorpay) resolve();
    });
  }

  return checkoutScriptPromise;
}

export const RazorpayCheckout: React.FC<RazorpayCheckoutProps> = ({
  orderId,
  keyId,
  amountPaise,
  currency = 'INR',
  description,
  customerName,
  customerEmail,
  customerPhone,
  onPaymentResponse,
  onDismiss,
  onError,
}) => {
  const [opening, setOpening] = useState(false);

  const openCheckout = async () => {
    if (opening) return;

    if (!orderId.trim() || !keyId.trim() || !Number.isSafeInteger(amountPaise) || amountPaise <= 0) {
      onError?.('Secure checkout unavailable: a valid backend order, public key, and amount are required.');
      return;
    }

    setOpening(true);

    try {
      await loadRazorpayCheckout();

      if (!window.Razorpay) {
        throw new Error('Razorpay Checkout is not available.');
      }

      const options: RazorpayCheckoutOptions = {
        key: keyId,
        amount: amountPaise,
        currency,
        name: 'PairUpToday',
        description,
        order_id: orderId,
        handler: async (response) => {
          if (
            !response.razorpay_payment_id ||
            !response.razorpay_order_id ||
            !response.razorpay_signature
          ) {
            onError?.('Checkout did not return all required payment verification fields.');
            return;
          }

          if (response.razorpay_order_id !== orderId) {
            onError?.('Payment order mismatch. Do not treat this payment as successful.');
            return;
          }

          await onPaymentResponse(response);
        },
        prefill: {
          name: customerName || '',
          email: customerEmail || '',
          contact: customerPhone || '',
        },
        theme: { color: '#6d28d9' },
        retry: { enabled: true },
        modal: {
          ondismiss: () => {
            setOpening(false);
            onDismiss?.();
          },
        },
      } as RazorpayCheckoutOptions & {
        prefill: { name: string; email: string; contact: string };
      };

      const checkout = new window.Razorpay(options);
      checkout.on('payment.failed', (event) => {
        setOpening(false);
        onError?.(
          event.error?.description ||
          event.error?.reason ||
          'Razorpay reported that the payment failed.'
        );
      });

      checkout.open();
    } catch (error) {
      setOpening(false);
      onError?.(
        error instanceof Error
          ? error.message
          : 'Unable to open secure Razorpay Checkout.'
      );
    }
  };

  return (
    <button
      type="button"
      onClick={openCheckout}
      disabled={opening}
      className="w-full rounded-xl bg-purple-700 px-4 py-3 font-bold text-white transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {opening ? 'Opening secure checkout…' : 'Pay securely with Razorpay'}
    </button>
  );
};

export default RazorpayCheckout;
